import React from 'react';
import { PrinterIcon, ExternalLinkIcon } from 'lucide-react';
interface Placement {
  id: string;
  location: string;
  page: string;
  wording: string;
  linkText: string;
  href: string;
  description: string;
}
const UTM = '?utm_source=meat_poultry_magazine';
const placements: Placement[] = [{
  id: 'A-1',
  location: 'Market Data Ticker',
  page: 'Homepage (/ and /home1)',
  wording: 'POWERED BY MeatMarket.ai',
  linkText: 'MeatMarket.ai',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Persistent attribution at the head of the continuous scrolling market-data ticker displayed near the top of the homepage.'
}, {
  id: 'A-2',
  location: 'USDA Market Pulse Strip',
  page: 'Homepage (/ and /home1)',
  wording: 'Powered by MeatMarket.ai',
  linkText: 'MeatMarket.ai',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Attribution in the section header of the USDA Market Pulse strip, accompanied by a "Launch Market-IQ" call-to-action.'
}, {
  id: 'A-3',
  location: 'Sidebar Market Widget',
  page: 'Article page (/article)',
  wording: 'Powered by MeatMarket.AI',
  linkText: 'MeatMarket.AI',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Attribution in the USDA Market Pulse sidebar widget alongside a "Launch Market-IQ" call-to-action.'
}, {
  id: 'A-4',
  location: 'Market IQ Landing — Introduction',
  page: 'Market IQ (/market-iq)',
  wording: 'Our integration with MeatMarket.ai ensures data is normalized, historically accurate, and updated in real-time.',
  linkText: 'MeatMarket.ai',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Inline attribution within the "Data Methodology & Sources" section of the Market IQ landing page.'
}, {
  id: 'A-5',
  location: 'Market IQ Landing — Reference Link',
  page: 'Market IQ (/market-iq)',
  wording: 'Visit MeatMarket.ai',
  linkText: 'Visit MeatMarket.ai',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Standalone call-to-action link within the "Data Methodology & Sources" section.'
}, {
  id: 'A-6',
  location: 'Market Dashboard — Chart Detail',
  page: 'Market IQ dashboards (/market-iq/beef, /pork, /chicken)',
  wording: 'Info icon → See More Details',
  linkText: 'meatmarket.ai/market-intelligence',
  href: `https://meatmarket.ai/market-intelligence${UTM}`,
  description: 'Information icon adjacent to each dashboard chart title, linking to MeatMarket.ai market intelligence details.'
}, {
  id: 'A-7',
  location: 'Market Dashboard — Data Link',
  page: 'Market IQ dashboards (/market-iq/beef, /pork, /chicken)',
  wording: 'View More Data',
  linkText: 'View More Data',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Call-to-action in each dashboard chart header linking out to the full MeatMarket.ai dataset.'
}, {
  id: 'A-8',
  location: 'Primal & Pricing Tables',
  page: 'Market IQ dashboards (Beef, Pork, Chicken)',
  wording: 'View Detailed Analysis',
  linkText: 'View Detailed Analysis',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Footer call-to-action beneath the Subprimals (Beef), Pork Primals, and Chicken Pricing tables.'
}, {
  id: 'A-9',
  location: 'Market IQ Layout Footer',
  page: 'All Market IQ pages',
  wording: 'MeatMarket.ai attribution',
  linkText: 'MeatMarket.ai',
  href: `https://meatmarket.ai/${UTM}`,
  description: 'Persistent MeatMarket.ai attribution in the shared footer of the Market IQ page layout.'
}];
export function ExhibitA() {
  const handlePrint = () => window.print();
  return <div className="min-h-screen w-full bg-gray-100 py-8 px-4 print:bg-white print:p-0" data-id="element-441">
      {/* Print control bar (hidden on print) */}
      <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between print:hidden" data-id="element-442">
        <a href="/" className="text-sm text-[#002f5f] hover:underline" data-id="element-443">
          &larr; Back to site
        </a>
        <button onClick={handlePrint} className="inline-flex items-center gap-2 px-4 py-2 bg-[#002f5f] text-white text-sm font-semibold rounded-md hover:bg-[#00274d] transition-colors" data-id="element-444">
          <PrinterIcon className="w-4 h-4" data-id="element-445" />
          Print / Export PDF
        </button>
      </div>

      {/* Document */}
      <div className="max-w-3xl mx-auto bg-white shadow-sm print:shadow-none border border-gray-200 print:border-0 px-10 py-12 print:px-0" data-id="element-446">
        {/* Header */}
        <header className="border-b-2 border-gray-900 pb-6 mb-8" data-id="element-447">
          <div className="flex items-baseline justify-between mb-4" data-id="element-448">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-gray-500" data-id="element-449">
              Exhibit A
            </span>
            <span className="text-xs text-gray-500" data-id="element-450">Reference document</span>
          </div>
          <h1 className="text-3xl font-['Oswald'] font-bold uppercase tracking-wide text-gray-900 mb-2" data-id="element-451">
            MeatMarket.ai Brand Placements
          </h1>
          <p className="text-sm text-gray-600 font-serif leading-relaxed" data-id="element-452">
            The following schedule catalogs each placement of the MeatMarket.ai
            name, mark, and outbound reference links integrated across the
            Meat+Poultry digital properties. All outbound links carry the UTM
            attribution parameter{' '}
            <code className="text-[11px] bg-gray-100 px-1 py-0.5 rounded" data-id="element-453">
              {UTM}
            </code>
            .
          </p>
        </header>

        {/* Brand Marks & Lockups */}
        <section className="mb-10 break-inside-avoid" data-id="element-454">
          <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-4" data-id="element-455">
            Approved Brand Marks & Lockups
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-456">
            <figure className="border border-gray-200 rounded-lg p-5 flex flex-col" data-id="element-457">
              <div className="flex-1 flex items-center justify-center bg-white rounded-md py-6 px-4 mb-3 min-h-[96px]" data-id="element-458">
                <img src="https://cdn.magicpatterns.com/uploads/oz5ZoYnYR5UjgdcDnLEJgq/Screenshot_2026-07-07_at_2.48.15_PM.png" alt="Powered by MeatMarket.ai text lockup" className="max-h-12 w-auto object-contain" data-id="element-459" />
              </div>
              <figcaption className="text-xs text-gray-600" data-id="element-460">
                <span className="font-semibold text-gray-900" data-id="element-461">
                  "Powered by" attribution lockup.
                </span>{' '}
                Used inline within data ticker, market strips, and widget
                headers.
              </figcaption>
            </figure>
            <figure className="border border-gray-200 rounded-lg p-5 flex flex-col" data-id="element-462">
              <div className="flex-1 flex items-center justify-center rounded-md py-6 px-4 mb-3 min-h-[96px]" data-id="element-463">
                <img src="https://cdn.magicpatterns.com/uploads/qeNJ2WjXdHLToPb7PiigtE/Screenshot_2026-07-07_at_2.58.50_PM.png" alt="MeatMarket.AI primary logo mark with MM icon" className="max-h-12 w-auto object-contain" data-id="element-464" />
              </div>
              <figcaption className="text-xs text-gray-600" data-id="element-465">
                <span className="font-semibold text-gray-900" data-id="element-466">
                  Primary logo mark.
                </span>{' '}
                The "MM" icon paired with the MeatMarket.AI wordmark, used for
                prominent brand placements.
              </figcaption>
            </figure>
          </div>
        </section>

        {/* Placement list */}
        <h2 className="text-xs font-bold uppercase tracking-[0.25em] text-gray-500 mb-4" data-id="element-467">
          Placement Schedule
        </h2>
        <ol className="space-y-6" data-id="element-468">
          {placements.map(p => <li key={p.id} className="break-inside-avoid border border-gray-200 rounded-lg p-5" data-id="element-469">
              <div className="flex items-start justify-between gap-4 mb-3" data-id="element-470">
                <div data-id="element-471">
                  <span className="text-[11px] font-bold uppercase tracking-widest text-[#002f5f]" data-id="element-472">
                    {p.id}
                  </span>
                  <h2 className="text-base font-bold text-gray-900 leading-tight" data-id="element-473">
                    {p.location}
                  </h2>
                </div>
                <span className="flex-shrink-0 text-[11px] text-gray-500 text-right max-w-[45%]" data-id="element-474">
                  {p.page}
                </span>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed mb-3" data-id="element-475">
                {p.description}
              </p>

              <dl className="text-xs space-y-1.5" data-id="element-476">
                <div className="flex gap-2" data-id="element-477">
                  <dt className="font-semibold text-gray-500 uppercase tracking-wide w-20 flex-shrink-0" data-id="element-478">
                    Wording
                  </dt>
                  <dd className="text-gray-900" data-id="element-479">"{p.wording}"</dd>
                </div>
                <div className="flex gap-2" data-id="element-480">
                  <dt className="font-semibold text-gray-500 uppercase tracking-wide w-20 flex-shrink-0" data-id="element-481">
                    Link
                  </dt>
                  <dd className="text-[#002f5f] break-all" data-id="element-482">
                    <a href={p.href} target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-1" data-id="element-483">
                      {p.href}
                      <ExternalLinkIcon className="w-3 h-3 flex-shrink-0 print:hidden" data-id="element-484" />
                    </a>
                  </dd>
                </div>
              </dl>
            </li>)}
        </ol>

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t border-gray-200 text-[11px] text-gray-400 flex items-center justify-between" data-id="element-485">
          <span data-id="element-486">Meat+Poultry &times; MeatMarket.ai — Exhibit A</span>
          <span data-id="element-487">{placements.length} placements catalogued</span>
        </footer>
      </div>
    </div>;
}